// src/merch/jersey.js
//
// Recolourable product art ("jersey templates", e.g. the ornament player).
//
// Built from assets made by tools/merch/prep_template.py:
//   <side>-regions.png  region index per pixel (jersey, stripes, pants…)
//   <side>-shade.png    baked shading, 128 = neutral
//   template.json       sizes, regions per side, face hole
// Each region gets a flat colour, then the shading multiplies over it
// (and lifts highlights a little), so the sculpted folds survive any
// colour. The result is ONE locked layer per side ("Jersey"), sitting
// under text / logos / drawings but ABOVE the face, which shows through
// the helmet's face hole.
//
// The chosen colours live on the layer itself (layer.template), so undo,
// autosave, Save project and orders all just work.

import { snapshot } from "valtio";
import { merch } from "./store";
import { getProduct, getSize, getSides, printAreaFor, BLEED_MM } from "./products";
import { resetHistory } from "./history";

export const REGION_LABELS = {
  jersey: "Jersey",
  "stripe-1": "Stripes",
  "stripe-2": "Chest band",
  "band-trim": "Band trim",
  "stripe-trim": "Stripe trim",
  "stripe-edge": "Stripe edge",
  "stripe-inner": "Inner stripe",
  yoke: "Shoulders",
  pants: "Pants",
  "pants-stripe": "Pant stripe",
  gloves: "Gloves",
  helmet: "Helmet",
};

const W = "#f4f4f0", BK = "#1b1b1d";
const OFF_JERSEY = "=jersey";
// Generic colour combos (no team names or logos).
export const PRESETS = [
  // your painting as painted: white jersey, black gloves + pants, white helmet
  { name: "Classic (as painted)", c: { jersey: W, "stripe-1": W, "stripe-2": BK, yoke: W, pants: "#2a2a2c", "pants-stripe": W, gloves: "#2a2a2c", helmet: W } },
  // Kingston Jr. Gaels (Jon / Sportsheadz): gold, navy sleeve + hem stripes,
  // no shoulder colour, no chest band, thin red trim along the stripes.
  { name: "Jr. Gaels", c: { _style: "gaels", jersey: "#f4b41a", "stripe-1": "#1b2a5c", "stripe-2": "=jersey", "band-trim": "=jersey", "stripe-trim": "#c8102e", yoke: "=jersey", pants: "#1b2a5c", "pants-stripe": "#f4b41a", gloves: "#1b2a5c", helmet: "#1b2a5c" } },
  { name: "Red & white", c: { jersey: "#b3242b", "stripe-1": W, "stripe-2": BK, yoke: W, pants: BK, "pants-stripe": "#b3242b", gloves: BK, helmet: BK } },
  { name: "Navy & gold", c: { jersey: "#1c2a4a", "stripe-1": "#c9a227", "stripe-2": W, yoke: "#c9a227", pants: "#1c2a4a", "pants-stripe": "#c9a227", gloves: "#1c2a4a", helmet: "#1c2a4a" } },
  { name: "Black & gold", c: { jersey: BK, "stripe-1": "#c9a227", "stripe-2": W, yoke: "#c9a227", pants: BK, "pants-stripe": "#c9a227", gloves: BK, helmet: BK } },
  { name: "Royal & white", c: { jersey: "#1f4fa3", "stripe-1": W, "stripe-2": "#1c2a4a", yoke: W, pants: "#16213d", "pants-stripe": W, gloves: "#1f4fa3", helmet: "#1f4fa3" } },
  { name: "Green & white", c: { jersey: "#1d6b3a", "stripe-1": W, "stripe-2": BK, yoke: W, pants: BK, "pants-stripe": "#1d6b3a", gloves: "#1d6b3a", helmet: BK } },
  { name: "Maroon & gold", c: { jersey: "#6d1f2f", "stripe-1": "#d8b24a", "stripe-2": W, yoke: "#d8b24a", pants: BK, "pants-stripe": "#d8b24a", gloves: "#6d1f2f", helmet: "#6d1f2f" } },
  { name: "Orange & black", c: { jersey: "#e0661b", "stripe-1": BK, "stripe-2": W, yoke: BK, pants: BK, "pants-stripe": "#e0661b", gloves: BK, helmet: BK } },
  { name: "White & red", c: { jersey: W, "stripe-1": "#b3242b", "stripe-2": BK, yoke: "#b3242b", pants: BK, "pants-stripe": "#b3242b", gloves: "#b3242b", helmet: "#b3242b" } },
  { name: "White & navy", c: { jersey: W, "stripe-1": "#1c2a4a", "stripe-2": "#c9a227", yoke: "#1c2a4a", pants: "#1c2a4a", "pants-stripe": W, gloves: "#1c2a4a", helmet: "#1c2a4a" } },
];
// the same combos without the chest band (it just takes the jersey colour)
export const PLAIN_PRESETS = PRESETS.map((p) => ({
  name: `${p.name}, no chest band`,
  c: { ...p.c, "stripe-2": OFF_JERSEY, "band-trim": OFF_JERSEY },
}));

// Minimum brightness per region: very dark colours swallow the painted
// detail on busy parts (helmet vents, glove padding), so black renders as a
// painted charcoal there. Hue is kept; only the value is lifted.
export const MIN_LUMA = { helmet: 95, gloves: 95 };
// every other region: a gentler floor so deep colours (navy, royal, maroon)
// keep their folds instead of filling in
const DEFAULT_MIN_LUMA = 68;
function liftDark(rgb, min) {
  const l = 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2];
  if (l >= min) return rgb;
  const add = min - l;
  return rgb.map((v) => Math.min(255, v + add));
}

/** Region colour, following "off" links ("=jersey" → the jersey colour). */
export function resolveColour(colours, name, depth = 0) {
  // band trim: follows the Stripes colour unless set, and goes away with the band
  if (name === "band-trim" && depth === 0) {
    const band = colours?.["stripe-2"];
    if (typeof band === "string" && band.startsWith("=")) return resolveColour(colours, band.slice(1), 1);
    if (colours?.["band-trim"] == null) return resolveColour(colours, "stripe-1", 1);
  }
  // stripe trim: off (blends into the jersey) unless a combo / pick sets it
  if (name === "stripe-trim" && depth === 0 && colours?.["stripe-trim"] == null) return resolveColour(colours, "jersey", 1);
  // optional thin stripes: off until picked (edge blends into the jersey,
  // inner stripe into the Stripes colour)
  if (name === "stripe-edge" && depth === 0 && colours?.[name] == null) return resolveColour(colours, "jersey", 1);
  if (name === "stripe-inner" && depth === 0 && colours?.[name] == null) return resolveColour(colours, "stripe-1", 1);
  const v = colours?.[name];
  if (typeof v === "string" && v.startsWith("=") && depth < 3) return resolveColour(colours, v.slice(1), depth + 1);
  return v;
}
// what "off" means per region: blends into the part it sits on
export const OFF_TARGET = { "stripe-1": "jersey", "stripe-2": "jersey", "band-trim": "jersey", "stripe-trim": "jersey", "stripe-edge": "jersey", "stripe-inner": "stripe-1", yoke: "jersey", "pants-stripe": "pants" };
const FIXED = "#dcdcdc"; // "fixed" parts (stick, cage…): neutral, shading only

/* ---------------- assets ---------------- */

const metaCache = new Map();
export function loadTemplateMeta(base) {
  if (!metaCache.has(base)) {
    const p = fetch(`${base}template.json`).then((r) => {
      if (!r.ok) throw new Error(`template.json ${r.status}`);
      return r.json();
    });
    p.catch(() => metaCache.delete(base));
    metaCache.set(base, p);
  }
  return metaCache.get(base);
}

const grayCache = new Map();
function loadGray(url) {
  if (!grayCache.has(url)) {
    grayCache.set(url, new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const d = ctx.getImageData(0, 0, c.width, c.height).data;
        const g = new Uint8Array(c.width * c.height);
        for (let i = 0; i < g.length; i++) g[i] = d[i * 4];
        res({ g, w: c.width, h: c.height });
      };
      img.onerror = () => { grayCache.delete(url); rej(new Error(`load ${url}`)); };
      img.src = url;
    }));
  }
  return grayCache.get(url);
}

const hex = (h) => {
  const s = (h || "#000").replace("#", "");
  const n = parseInt(s.length === 3 ? s.split("").map((x) => x + x).join("") : s, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Render one art layer (`prefix` = "front" or "front-helmet") with
 *  `colours` → { url, w, h }. `withAlpha` uses <prefix>-alpha.png for soft
 *  painted edges (helmet overlay). */
export async function renderLayerArt(base, meta, prefix, colours, withAlpha = false, withHilite = false, style = null) {
  const sfx = style ? `-${style}` : "";
  const [R, S, A, HL] = await Promise.all([
    loadGray(`${base}${prefix}-regions${sfx}.png`),
    loadGray(`${base}${prefix}-shade${sfx}.png`),
    withAlpha ? loadGray(`${base}${prefix}-alpha.png`).catch(() => null) : null,
    withHilite ? loadGray(`${base}${prefix}-hl.png`).catch(() => null) : null,
  ]);
  const { w, h } = R;
  // colour per region index
  const lut = meta.regions.map((name) => (name === "none" ? null : liftDark(hex(name === "fixed" ? FIXED : resolveColour(colours, name) || FIXED), MIN_LUMA[name] ?? DEFAULT_MIN_LUMA)));
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  const img = ctx.createImageData(w, h);
  const d = img.data;
  const sg = S.g, rg = R.g, sameSize = S.w === w && S.h === h;
  const ag = A && A.w === w && A.h === h ? A.g : null;
  const hg = HL && HL.w === w && HL.h === h ? HL.g : null; // painted highlights
  for (let i = 0; i < rg.length; i++) {
    const col = lut[rg[i]];
    if (!col) continue;
    const s = (sameSize ? sg[i] : 128) / 128;
    const o = i * 4;
    for (let k = 0; k < 3; k++) {
      const v = col[k];
      // multiply the shading; highlights (>1) lift gently toward white
      let c = s <= 1 ? v * s : v + Math.min(s - 1, 0.6) * (255 - v) * 0.45;
      if (hg && hg[i]) c += (255 - c) * (hg[i] / 255); // screen the shine on top
      d[o + k] = c;
    }
    d[o + 3] = ag ? ag[i] : 255;
  }
  ctx.putImageData(img, 0, 0);
  let url = c.toDataURL("image/webp", 0.92);
  if (!url.startsWith("data:image/webp")) url = c.toDataURL("image/png"); // older Safari
  return { url, w, h };
}

// face masks / hair: drawn on top of everything (see prep_template.py)
export const MASKS = ["visor", "cage"];
export const VISOR_TINTS = { clear: null, smoke: ["#262b33", 0.5], dark: ["#0e1115", 0.86] };
export const CAGE_COLOURS = [["White", "#f4f4f0"], ["Black", "#1b1b1d"], ["Chrome", "#9aa0a6"], ["Gold", "#c9a227"]];
export const HAIRS = ["ponytail", "braid"];
export const HAIR_COLOURS = [
  ["Blonde", "#d8b36a"], ["Light brown", "#9c6b3c"], ["Brown", "#5b3a22"],
  ["Black", "#211c19"], ["Auburn", "#8a3a1c"], ["Red", "#b5502a"], ["Grey", "#a09b94"],
];

function loadRGBA(url) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(img, 0, 0);
      res({ d: ctx.getImageData(0, 0, c.width, c.height).data, w: c.width, h: c.height });
    };
    img.onerror = () => rej(new Error(`load ${url}`));
    img.src = url;
  });
}

/** Render a mask / hair overlay → { canvas, w, h }. */
export async function renderOverlay(base, side, name, ov, colours) {
  const prefix = `${base}${side.file}-${name}`;
  const HL = ov.hilite ? await loadGray(`${prefix}-hl.png`).catch(() => null) : null;
  let w, h, out;
  if (ov.recolour) {
    const [S, A] = await Promise.all([loadGray(`${prefix}-shade.png`), loadGray(`${prefix}-alpha.png`)]);
    w = S.w; h = S.h;
    const isHair = HAIRS.includes(name);
    const want = isHair ? colours.hair || "#5b3a22" : colours.cage === "=helmet" ? resolveColour(colours, "helmet") : colours.cage || "#f4f4f0";
    // hair: gentler floor (black hair keeps its strands); cage like the helmet
    const col = liftDark(hex(want), isHair ? 70 : 95);
    out = new Uint8ClampedArray(w * h * 4);
    for (let i = 0; i < w * h; i++) {
      const a = A.g[i];
      if (!a) continue;
      const s = S.g[i] / 128, o = i * 4;
      for (let k = 0; k < 3; k++) {
        const v = col[k];
        let c = s <= 1 ? v * s : v + Math.min(s - 1, 0.6) * (255 - v) * 0.45;
        if (HL && HL.g[i]) c += (255 - c) * (HL.g[i] / 255);
        out[o + k] = c;
      }
      out[o + 3] = a;
    }
  } else {
    // painted as-is (visor glare); optional tint under the glare, filling the visor's shape
    const P = await loadRGBA(`${prefix}-ov.png`);
    w = P.w; h = P.h;
    const tint = name === "visor" ? VISOR_TINTS[colours._visorTint || "clear"] : null;
    const tc = tint ? hex(tint[0]) : null;
    out = new Uint8ClampedArray(w * h * 4);
    for (let i = 0; i < w * h; i++) {
      const o = i * 4, ga = P.d[o + 3] / 255;
      if (!ga) continue;
      const ta = tint ? tint[1] * Math.min(1, ga * 5) : 0; // solid shape from the glare's soft alpha
      const oa = ga + ta * (1 - ga);
      for (let k = 0; k < 3; k++) {
        let c = (P.d[o + k] * ga + (tc ? tc[k] : 0) * ta * (1 - ga)) / (oa || 1);
        if (HL && HL.g[i]) c += (255 - c) * (HL.g[i] / 255);
        out[o + k] = c;
      }
      out[o + 3] = oa * 255;
    }
  }
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  c.getContext("2d").putImageData(new ImageData(out, w, h), 0, 0);
  return { canvas: c, w, h };
}

/** All chosen overlays for a side (face mask on the front, hair on the
 *  back…) flattened into one image → { url, w, h, names } or null. */
async function renderOverlays(base, side, colours, which) {
  const names = which.filter((n) => n && side.overlays?.[n]);
  if (!names.length) return null;
  const parts = [];
  for (const n of names) parts.push(await renderOverlay(base, side, n, side.overlays[n], colours));
  const { w, h } = parts[0];
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  for (const p of parts) ctx.drawImage(p.canvas, 0, 0, w, h);
  let url = c.toDataURL("image/webp", 0.92);
  if (!url.startsWith("data:image/webp")) url = c.toDataURL("image/png");
  return { url, w, h, names };
}

/** Back-compat: the body art of one side. */
export const renderSide = (base, meta, zoneId, colours) =>
  renderLayerArt(base, meta, meta.sides[zoneId].file, colours);

/* ---------------- layers ---------------- */

// kinds: "jersey" = body art (bottom), "jersey-helmet" = helmet overlay
// (on top of the face). Both are locked and managed here.
export const isJersey = (L) => L?.kind === "jersey" || L?.kind === "jersey-helmet" || L?.kind === "jersey-mask" || L?.kind === "jersey-hair";
const isBody = (L) => L?.kind === "jersey";
const isHelmet = (L) => L?.kind === "jersey-helmet";
const isMask = (L) => L?.kind === "jersey-mask";
const isHair = (L) => L?.kind === "jersey-hair";

function zonesOf(product) {
  const size = getSize(product, merch.sizeId);
  return getSides(product, size, merch.hand);
}

/** Current colours (from any side's jersey layer), or null if off. */
export function currentJersey(s = snapshot(merch)) {
  const all = [s.layers, ...Object.values(s.sideLayers || {})];
  for (const arr of all) {
    const L = (arr || []).find(isBody);
    if (L) return L.template?.colours || null;
  }
  return null;
}

/** Order: hair (starts on top, over names / numbers) → your layers →
 *  face mask → helmet → face(s) → jersey body. Hair keeps wherever the
 *  user has moved it to. */
function placeJersey(layers, body, helmet, mask = null, hair = null) {
  const rest = layers.filter((x) => !isJersey(x));
  const faces = rest.filter((x) => x.kind === "face");
  const others = rest.filter((x) => x.kind !== "face");
  if (hair) {
    const oldIdx = layers.findIndex(isHair);
    // its slot among the user's layers (how many of them sat above it)
    const pos = oldIdx < 0 ? 0 : layers.slice(0, oldIdx).filter((x) => !isJersey(x) && x.kind !== "face").length;
    others.splice(Math.min(pos, others.length), 0, hair);
  }
  return [...others, ...(mask ? [mask] : []), ...(helmet ? [helmet] : []), ...faces, ...(body ? [body] : [])];
}

function setSideLayers(zoneId, fn) {
  if (zoneId === merch.side) merch.layers = fn(merch.layers);
  else merch.sideLayers = { ...merch.sideLayers, [zoneId]: fn(merch.sideLayers[zoneId] || []) };
}

function artLayer(old, { id, name, kind, url, w, h, z, product, colours }) {
  const area = printAreaFor(z.sticker);
  const aw = area.x1 - area.x0, ah = area.y1 - area.y0;
  const Hmm = z.sticker.heightMm + 2 * BLEED_MM, Wmm = z.sticker.widthMm + 2 * BLEED_MM;
  // the art covers trim + bleed; the engine fits the long edge to `scale`
  const scale = w / h >= 1 ? aw * (Wmm / z.sticker.widthMm) : ah * (Hmm / z.sticker.heightMm);
  return {
    ...(old || {}),
    id: old?.id || id,
    name,
    url,
    visible: old?.visible ?? true,
    opacity: 1,
    blend: "block",
    edgeClip: 0,
    kind,
    locked: true,
    template: { id: product.id, colours: { ...colours } },
    transform: { tx: (area.x0 + area.x1) / 2, ty: (area.y0 + area.y1) / 2, scale, rot: 0 },
  };
}

let seq = 0;
/** Turn the jersey on / recolour it (all sides), or off with `null`. */
export async function applyJersey(colours) {
  const product = getProduct(merch.productId);
  const tpl = product.template;
  if (!tpl) return;
  const my = ++seq;
  if (!colours) {
    for (const z of zonesOf(product)) setSideLayers(z.id, (ls) => placeJersey(ls, null, null, null, null));
    if (merch.selectedId && !merch.layers.some((l) => l.id === merch.selectedId)) merch.selectedId = null;
    merch.sideRev++; merch.rev++;
    return;
  }
  const meta = await loadTemplateMeta(tpl.base);
  const rendered = [];
  for (const z of zonesOf(product)) {
    const side = meta.sides[z.id];
    if (!side) continue;
    // stripe-layout style (e.g. "gaels"): a different colour map, same painting
    const style = colours._style && side.styles?.includes(colours._style) ? colours._style : null;
    const body = await renderLayerArt(tpl.base, meta, side.file, colours, false, !!side.hilite, style);
    const helmet = side.helmet ? await renderLayerArt(tpl.base, meta, `${side.file}-helmet`, colours, side.helmet.alpha, !!side.helmet.hilite) : null;
    const mask = await renderOverlays(tpl.base, side, colours, [colours._mask]);
    const hairArt = await renderOverlays(tpl.base, side, colours, [colours._hair]);
    const title = (n) => n[0].toUpperCase() + n.slice(1);
    rendered.push({ z, body, helmet, mask, maskName: mask && title(mask.names[0]), hairArt, hairName: hairArt && title(hairArt.names[0]) });
  }
  if (my !== seq) return; // a newer colour pick won
  for (const { z, body, helmet, mask, maskName, hairArt, hairName } of rendered) {
    setSideLayers(z.id, (ls) => {
      const B = artLayer(ls.find(isBody), { id: `jersey-${z.id}`, name: "Jersey", kind: "jersey", ...body, z, product, colours });
      const H = helmet
        ? artLayer(ls.find(isHelmet), { id: `helmet-${z.id}`, name: "Helmet", kind: "jersey-helmet", ...helmet, z, product, colours })
        : null;
      const M = mask
        ? artLayer(ls.find(isMask), { id: `mask-${z.id}`, name: maskName, kind: "jersey-mask", url: mask.url, w: mask.w, h: mask.h, z, product, colours })
        : null;
      const oldHair = ls.find(isHair);
      const HR = hairArt
        ? { ...artLayer(oldHair, { id: `hair-${z.id}`, name: hairName, kind: "jersey-hair", url: hairArt.url, w: hairArt.w, h: hairArt.h, z, product, colours }),
            pinTop: oldHair ? oldHair.pinTop !== false : true }
        : null;
      return placeJersey(ls, B, H, M, HR);
    });
  }
  merch.sideRev++;
  merch.rev++;
}

/** Products with template art start WITH the jersey on (looks finished
 *  from the first second). Only when the whole design is empty, so removing
 *  it (to do just a name / number, or a colouring sheet) sticks. */
let ensuring = false;
export async function ensureDefaultJersey() {
  const product = getProduct(merch.productId);
  if (!product.template || ensuring || !merch.loaded) return;
  const empty = zonesOf(product).every((z) => !(z.id === merch.side ? merch.layers : merch.sideLayers[z.id] || []).length);
  if (!empty || merch.paint.strokes) return;
  ensuring = true;
  try {
    await applyJersey(PRESETS[0].c);
    resetHistory(); // the starting jersey isn't an undo step
  } catch (e) {
    console.warn("[jersey] default failed", e);
  } finally {
    ensuring = false;
  }
}

/** After adding a face: keep it between the jersey and the helmet. */
export function sinkFaceUnderJersey() {
  if (!merch.layers.some(isJersey)) return;
  merch.layers = placeJersey(merch.layers, merch.layers.find(isBody), merch.layers.find(isHelmet), merch.layers.find(isMask), merch.layers.find(isHair));
  merch.rev++;
}
